import React, { useState, useEffect } from 'react';
import { FaFolderOpen, FaTimes, FaTrash } from 'react-icons/fa';
import { getSavedFiles, loadJsonFile, deleteJsonFile } from './localStorageUtils';
import './LoadModal.css';

const LoadModal = ({ isOpen, onClose, onLoadFile, isDarkMode }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadFiles();
        }
    }, [isOpen]);

    const loadFiles = () => {
        setLoading(true);
        setError('');
        try {
            const savedFiles = getSavedFiles();
            setFiles(savedFiles);
        } catch (err) {
            setError('Failed to load saved files');
        } finally {
            setLoading(false);
        }
    };

    const handleLoadFile = async (fileId) => {
        try {
            const result = loadJsonFile(fileId);
            if (result.success) {
                onLoadFile && onLoadFile(result.file.content);
                onClose();
            } else {
                setError(result.error || 'Failed to load file');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        }
    };

    const handleDeleteFile = async (fileId, fileName) => {
        try {
            const result = deleteJsonFile(fileId);
            if (result.success) {
                loadFiles(); // Refresh the list
            } else {
                setError(result.error || 'Failed to delete file');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        }
    };

    const handleClose = () => {
        setFiles([]);
        setError('');
        onClose();
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Unknown date';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content load-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        <FaFolderOpen className="modal-icon" />
                        Load JSON File
                    </h3>
                    <button className="close-btn" onClick={handleClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading saved files...</p>
                        </div>
                    ) : files.length === 0 ? (
                        <div className="empty-state">
                            <FaFolderOpen className="empty-icon" />
                            <p>No saved files found</p>
                            <small>Save some JSON files to see them here</small>
                        </div>
                    ) : (
                        <div className="files-list">
                            {files.map((file) => (
                                <div key={file.id} className="file-item">
                                    <div className="file-info" onClick={() => handleLoadFile(file.id)}>
                                        <div className="file-name">{file.name}</div>
                                        <div className="file-date">{formatDate(file.createdAt)}</div>
                                    </div>
                                    <button
                                        className="delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFile(file.id, file.name);
                                        }}
                                        title="Delete file"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}
                </div>
            </div>
        </div>
    );
};

export default LoadModal;
